/**
 * Phase 8 — Poster template configs.
 *
 * Each template exposes:
 *   • meta        — id, name, category hints
 *   • defaults    — headline / sub-line / accent color / font style
 *   • palette     — 6 accent swatches for the color picker
 *   • draw(ctx, ...args)  — pure canvas-drawing function
 *
 * `draw` is called with a normalized state object so a single implementation
 * can render the full 1080×1350 poster off-screen (for the export blob) and
 * on-screen (for the live preview via CSS scaling).
 */

export const CANVAS_W = 1080;
export const CANVAS_H = 1350;

// --- shared helpers ---------------------------------------------------------

const roundRect = (ctx, x, y, w, h, r) => {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.lineTo(x + w - rr, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
    ctx.lineTo(x + w, y + h - rr);
    ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
    ctx.lineTo(x + rr, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
    ctx.lineTo(x, y + rr);
    ctx.quadraticCurveTo(x, y, x + rr, y);
    ctx.closePath();
};

const linearGrad = (ctx, x1, y1, x2, y2, stops) => {
    const g = ctx.createLinearGradient(x1, y1, x2, y2);
    stops.forEach(([o, c]) => g.addColorStop(o, c));
    return g;
};

const wrappedText = (ctx, text, x, y, maxWidth, lineHeight) => {
    const words = String(text || "").split(/\s+/);
    let line = "";
    let cy = y;
    for (const w of words) {
        const test = line ? line + " " + w : w;
        if (ctx.measureText(test).width > maxWidth && line) {
            ctx.fillText(line, x, cy);
            cy += lineHeight;
            line = w;
        } else {
            line = test;
        }
    }
    if (line) ctx.fillText(line, x, cy);
    return cy;
};

/** Font stacks per style — bundled via CSS @import in poster-studio.css. */
export const FONT_FOR_STYLE = {
    BOLD: "'Anton', 'Impact', 'Bebas Neue', sans-serif",
    MODERN: "'Poppins', 'Inter', sans-serif",
    PLAYFUL: "'Fredoka', 'Comic Sans MS', 'Baloo 2', sans-serif",
};

/** Paint the product photo covering (x,y,w,h) with object-fit: cover. */
const paintPhotoCover = (ctx, img, x, y, w, h, zoom = 1) => {
    if (!img) return;
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;
    const targetRatio = w / h;
    const imgRatio = iw / ih;
    let sw, sh, sx, sy;
    if (imgRatio > targetRatio) {
        sh = ih / zoom;
        sw = sh * targetRatio;
        sx = (iw - sw) / 2;
        sy = (ih - sh) / 2;
    } else {
        sw = iw / zoom;
        sh = sw / targetRatio;
        sx = (iw - sw) / 2;
        sy = (ih - sh) / 2;
    }
    ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
};

// --- templates --------------------------------------------------------------

const T = (fn) => fn; // labelling helper

const drawStarburst = (ctx, cx, cy, r, spikes, fill) => {
    ctx.beginPath();
    for (let i = 0; i < spikes * 2; i++) {
        const angle = (i * Math.PI) / spikes;
        const rr = i % 2 === 0 ? r : r * 0.72;
        const x = cx + rr * Math.cos(angle);
        const y = cy + rr * Math.sin(angle);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
};

const TEMPLATES = {
    BIG_SALE: {
        id: "BIG_SALE",
        name: "Big Sale",
        category_hint: ["fashion", "supermarkets", "electronics"],
        defaults: { headline: "MEGA SALE", sub: "Only this weekend", accent: "#DC2626", font: "BOLD" },
        palette: ["#DC2626", "#EA580C", "#F97316", "#B91C1C", "#7C2D12", "#F59E0B"],
        draw: T((ctx, s) => {
            // Background gradient
            ctx.fillStyle = linearGrad(ctx, 0, 0, CANVAS_W, CANVAS_H, [[0, s.accent], [1, "#111"]]);
            ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            // Photo full-bleed with darkening overlay
            if (s.photo) paintPhotoCover(ctx, s.photo, 0, 0, CANVAS_W, CANVAS_H, s.zoom);
            ctx.fillStyle = "rgba(0,0,0,0.55)";
            ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            // Diagonal accent stripe
            ctx.save();
            ctx.translate(CANVAS_W, 0);
            ctx.rotate(Math.PI / 6);
            ctx.fillStyle = s.accent;
            ctx.fillRect(-160, -300, 340, 1800);
            ctx.restore();
            // Headline
            ctx.textAlign = "left";
            ctx.fillStyle = "#FFF";
            ctx.font = `900 110px ${FONT_FOR_STYLE[s.font]}`;
            wrappedText(ctx, s.headline.toUpperCase(), 70, 200, CANVAS_W - 200, 110);
            // Giant discount
            ctx.textAlign = "center";
            ctx.font = `900 260px ${FONT_FOR_STYLE[s.font]}`;
            ctx.fillStyle = "#FFC300";
            ctx.fillText(s.discount || "SALE", CANVAS_W / 2, 780);
            // Sub
            if (s.sub) {
                ctx.font = `700 44px ${FONT_FOR_STYLE.MODERN}`;
                ctx.fillStyle = "#F1F1F1";
                ctx.fillText(s.sub, CANVAS_W / 2, 900);
            }
        }),
    },

    BOGO: {
        id: "BOGO",
        name: "Buy 1 Get 1",
        category_hint: ["fashion", "restaurants", "beauty"],
        defaults: { headline: "Buy 1 Get 1", sub: "FREE", accent: "#F59E0B", font: "BOLD" },
        palette: ["#F59E0B", "#FBBF24", "#F97316", "#DC2626", "#EA580C", "#A16207"],
        draw: T((ctx, s) => {
            ctx.fillStyle = linearGrad(ctx, 0, 0, 0, CANVAS_H, [[0, "#FFF7CC"], [1, s.accent]]);
            ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            // Product photo card (left)
            const px = 60, py = 220, pw = 480, ph = 620;
            ctx.save();
            roundRect(ctx, px, py, pw, ph, 32); ctx.clip();
            if (s.photo) paintPhotoCover(ctx, s.photo, px, py, pw, ph, s.zoom);
            else { ctx.fillStyle = "#FFF"; ctx.fillRect(px, py, pw, ph); }
            ctx.restore();
            ctx.save();
            roundRect(ctx, px, py, pw, ph, 32); ctx.lineWidth = 8; ctx.strokeStyle = "#111"; ctx.stroke();
            ctx.restore();
            // Right column text
            ctx.textAlign = "left";
            ctx.fillStyle = "#111";
            ctx.font = `900 84px ${FONT_FOR_STYLE[s.font]}`;
            wrappedText(ctx, s.headline, 580, 320, 440, 92);
            // FREE burst
            drawStarburst(ctx, 800, 720, 190, 12, "#DC2626");
            ctx.textAlign = "center";
            ctx.fillStyle = "#FFF";
            ctx.font = `900 108px ${FONT_FOR_STYLE.BOLD}`;
            ctx.fillText(s.sub || "FREE", 800, 750);
            // Discount pill
            if (s.discount) {
                ctx.fillStyle = "#111";
                roundRect(ctx, 580, 900, 440, 90, 45); ctx.fill();
                ctx.font = `800 52px ${FONT_FOR_STYLE.MODERN}`;
                ctx.fillStyle = "#FFC300";
                ctx.fillText(s.discount, 800, 962);
            }
        }),
    },

    NEW_ARRIVAL: {
        id: "NEW_ARRIVAL",
        name: "New Arrival",
        category_hint: ["fashion", "electronics", "furniture"],
        defaults: { headline: "New Arrival", sub: "Just landed in store", accent: "#0F766E", font: "MODERN" },
        palette: ["#0F766E", "#059669", "#14B8A6", "#0EA5E9", "#111827", "#78716C"],
        draw: T((ctx, s) => {
            ctx.fillStyle = "#F8FAF7";
            ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            // Accent bar top
            ctx.fillStyle = s.accent;
            ctx.fillRect(0, 0, CANVAS_W, 20);
            // Circle photo center
            const cx = CANVAS_W / 2, cy = 640, r = 380;
            ctx.save();
            ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.clip();
            if (s.photo) paintPhotoCover(ctx, s.photo, cx - r, cy - r, r * 2, r * 2, s.zoom);
            else { ctx.fillStyle = "#E5E7EB"; ctx.fillRect(cx - r, cy - r, r * 2, r * 2); }
            ctx.restore();
            ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.lineWidth = 6; ctx.strokeStyle = s.accent; ctx.stroke();
            // Small caps headline
            ctx.textAlign = "center";
            ctx.fillStyle = s.accent;
            ctx.font = `800 44px ${FONT_FOR_STYLE.MODERN}`;
            ctx.fillText((s.headline || "New Arrival").toUpperCase(), CANVAS_W / 2, 180);
            // Product name below (large serif fallback)
            ctx.fillStyle = "#111";
            ctx.font = `800 88px 'Playfair Display', 'Georgia', serif`;
            wrappedText(ctx, s.discount || s.sub || "", 100, 1100, CANVAS_W - 200, 92);
            // Sub tagline bottom
            ctx.font = `500 34px ${FONT_FOR_STYLE.MODERN}`;
            ctx.fillStyle = "#4B5563";
            ctx.fillText(s.sub || "", CANVAS_W / 2, 1210);
        }),
    },

    FLASH_DEAL: {
        id: "FLASH_DEAL",
        name: "Flash Deal",
        category_hint: ["electronics", "restaurants"],
        defaults: { headline: "Flash Deal", sub: "Ends soon!", accent: "#7C3AED", font: "BOLD" },
        palette: ["#7C3AED", "#1E40AF", "#0F172A", "#DC2626", "#F59E0B", "#059669"],
        draw: T((ctx, s) => {
            // Navy gradient
            ctx.fillStyle = linearGrad(ctx, 0, 0, CANVAS_W, CANVAS_H, [[0, "#0F172A"], [1, s.accent]]);
            ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            // Lightning bolt SVG-like path
            ctx.save();
            ctx.fillStyle = "#FBBF24";
            ctx.beginPath();
            ctx.moveTo(900, 90); ctx.lineTo(700, 500); ctx.lineTo(820, 500);
            ctx.lineTo(660, 900); ctx.lineTo(960, 400); ctx.lineTo(840, 400);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
            // Product photo faded left
            if (s.photo) { ctx.globalAlpha = 0.35; paintPhotoCover(ctx, s.photo, 0, 0, CANVAS_W, CANVAS_H, s.zoom); ctx.globalAlpha = 1; }
            ctx.textAlign = "left";
            ctx.fillStyle = "#FFF";
            ctx.font = `900 130px ${FONT_FOR_STYLE[s.font]}`;
            wrappedText(ctx, s.headline.toUpperCase(), 70, 240, 650, 130);
            // Discount tile
            ctx.fillStyle = "#FBBF24";
            roundRect(ctx, 70, 560, 640, 260, 36); ctx.fill();
            ctx.fillStyle = "#111";
            ctx.textAlign = "center";
            ctx.font = `900 180px ${FONT_FOR_STYLE[s.font]}`;
            ctx.fillText(s.discount || "50% OFF", 390, 730);
            // Sub
            ctx.textAlign = "left";
            ctx.font = `800 48px ${FONT_FOR_STYLE.MODERN}`;
            ctx.fillStyle = "#FBBF24";
            ctx.fillText(`⚡ ${s.sub || ""}`, 70, 900);
        }),
    },

    FESTIVAL: {
        id: "FESTIVAL",
        name: "Festival Offer",
        category_hint: ["fashion", "jewellery", "supermarkets"],
        defaults: { headline: "Onam Special", sub: "Blessings & savings", accent: "#B45309", font: "MODERN" },
        palette: ["#B45309", "#DC2626", "#7C2D12", "#F59E0B", "#B91C1C", "#0F766E"],
        draw: T((ctx, s) => {
            ctx.fillStyle = linearGrad(ctx, 0, 0, 0, CANVAS_H, [[0, "#FEF3C7"], [0.6, s.accent], [1, "#7C2D12"]]);
            ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            // Mandala pattern (concentric ovals top corners)
            for (let i = 0; i < 8; i++) {
                ctx.beginPath();
                ctx.arc(120, 150, 40 + i * 30, 0, Math.PI * 2);
                ctx.strokeStyle = `rgba(252, 211, 77, ${0.35 - i * 0.03})`;
                ctx.lineWidth = 2;
                ctx.stroke();
                ctx.beginPath();
                ctx.arc(CANVAS_W - 120, 150, 40 + i * 30, 0, Math.PI * 2);
                ctx.stroke();
            }
            // Product photo inside gold frame
            const px = 140, py = 380, pw = CANVAS_W - 280, ph = 540;
            ctx.save();
            roundRect(ctx, px, py, pw, ph, 28); ctx.clip();
            if (s.photo) paintPhotoCover(ctx, s.photo, px, py, pw, ph, s.zoom);
            else { ctx.fillStyle = "#FEF3C7"; ctx.fillRect(px, py, pw, ph); }
            ctx.restore();
            ctx.save();
            roundRect(ctx, px, py, pw, ph, 28);
            ctx.lineWidth = 14; ctx.strokeStyle = "#FBBF24"; ctx.stroke();
            ctx.restore();
            // Headline top
            ctx.textAlign = "center";
            ctx.fillStyle = "#7C2D12";
            ctx.font = `900 96px 'Playfair Display', ${FONT_FOR_STYLE[s.font]}`;
            ctx.fillText(s.headline, CANVAS_W / 2, 260);
            // Discount below
            ctx.fillStyle = "#FEF3C7";
            ctx.font = `900 120px ${FONT_FOR_STYLE.BOLD}`;
            ctx.fillText(s.discount || "", CANVAS_W / 2, 1060);
            // Sub
            ctx.fillStyle = "#FEF3C7";
            ctx.font = `600 38px ${FONT_FOR_STYLE.MODERN}`;
            ctx.fillText(s.sub || "", CANVAS_W / 2, 1140);
        }),
    },

    GRAND_OPENING: {
        id: "GRAND_OPENING",
        name: "Grand Opening",
        category_hint: ["restaurants", "beauty", "fashion", "bakeries"],
        defaults: { headline: "GRAND OPENING", sub: "You're invited!", accent: "#7C3AED", font: "MODERN" },
        palette: ["#7C3AED", "#EC4899", "#F97316", "#0F766E", "#111827", "#F59E0B"],
        draw: T((ctx, s) => {
            ctx.fillStyle = "#FFF7ED";
            ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            // Confetti
            const conf = ["#F97316", "#7C3AED", "#EC4899", "#0F766E", "#F59E0B"];
            for (let i = 0; i < 60; i++) {
                const x = Math.random() * CANVAS_W;
                const y = Math.random() * CANVAS_H;
                ctx.fillStyle = conf[i % conf.length];
                ctx.fillRect(x, y, 12, 4);
            }
            // Ribbon top
            ctx.fillStyle = s.accent;
            ctx.beginPath();
            ctx.moveTo(0, 120); ctx.lineTo(CANVAS_W, 60); ctx.lineTo(CANVAS_W, 260); ctx.lineTo(0, 320);
            ctx.closePath(); ctx.fill();
            ctx.textAlign = "center";
            ctx.fillStyle = "#FFF";
            ctx.font = `900 92px ${FONT_FOR_STYLE[s.font]}`;
            ctx.fillText(s.headline.toUpperCase(), CANVAS_W / 2, 220);
            // Photo card center
            const px = 120, py = 400, pw = CANVAS_W - 240, ph = 560;
            ctx.save();
            roundRect(ctx, px, py, pw, ph, 24); ctx.clip();
            if (s.photo) paintPhotoCover(ctx, s.photo, px, py, pw, ph, s.zoom);
            else { ctx.fillStyle = "#FFF"; ctx.fillRect(px, py, pw, ph); }
            ctx.restore();
            // Sub — auto-blends with business name/district when provided
            ctx.fillStyle = "#111";
            ctx.font = `800 56px ${FONT_FOR_STYLE.MODERN}`;
            ctx.fillText(s.sub || "You're invited!", CANVAS_W / 2, 1050);
            if (s.discount) {
                ctx.font = `900 76px ${FONT_FOR_STYLE.BOLD}`;
                ctx.fillStyle = s.accent;
                ctx.fillText(s.discount, CANVAS_W / 2, 1140);
            }
        }),
    },

    COMBO_MEAL: {
        id: "COMBO_MEAL",
        name: "Combo Meal",
        category_hint: ["restaurants", "bakeries"],
        defaults: { headline: "COMBO", sub: "Perfect together", accent: "#EA580C", font: "BOLD" },
        palette: ["#EA580C", "#DC2626", "#B45309", "#78350F", "#F59E0B", "#0F766E"],
        draw: T((ctx, s) => {
            ctx.fillStyle = linearGrad(ctx, 0, 0, 0, CANVAS_H, [[0, "#FEDBB0"], [1, "#FFE7C2"]]);
            ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            // Top pill
            ctx.fillStyle = s.accent;
            roundRect(ctx, CANVAS_W / 2 - 180, 120, 360, 100, 50); ctx.fill();
            ctx.textAlign = "center";
            ctx.fillStyle = "#FFF";
            ctx.font = `900 60px ${FONT_FOR_STYLE[s.font]}`;
            ctx.fillText(s.headline || "COMBO", CANVAS_W / 2, 190);
            // Two circles + plus sign
            const cy = 620, r = 200;
            [{ cx: 320 }, { cx: CANVAS_W - 320 }].forEach(({ cx }) => {
                ctx.save();
                ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.clip();
                if (s.photo) paintPhotoCover(ctx, s.photo, cx - r, cy - r, r * 2, r * 2, s.zoom);
                else { ctx.fillStyle = "#FFF"; ctx.fillRect(cx - r, cy - r, r * 2, r * 2); }
                ctx.restore();
                ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
                ctx.lineWidth = 8; ctx.strokeStyle = s.accent; ctx.stroke();
            });
            ctx.fillStyle = s.accent;
            ctx.font = `900 150px ${FONT_FOR_STYLE.BOLD}`;
            ctx.fillText("+", CANVAS_W / 2, cy + 50);
            // Big discount at bottom
            ctx.fillStyle = "#111";
            ctx.font = `900 140px ${FONT_FOR_STYLE[s.font]}`;
            ctx.fillText(s.discount || "", CANVAS_W / 2, 1050);
            ctx.font = `600 40px ${FONT_FOR_STYLE.MODERN}`;
            ctx.fillStyle = "#7C2D12";
            ctx.fillText(s.sub || "", CANVAS_W / 2, 1130);
        }),
    },
};

export const TEMPLATE_LIST = Object.values(TEMPLATES);
export const TEMPLATE_MAP = TEMPLATES;

/** Bottom-corner branding overlay used by every template. */
export const drawBranding = (ctx, wordmarkImg, shopLogoImg, shopName) => {
    // OfferNest wordmark bottom-left
    if (wordmarkImg) {
        const w = 220, h = w * (wordmarkImg.naturalHeight / wordmarkImg.naturalWidth || 0.25);
        ctx.save();
        ctx.shadowColor = "rgba(0,0,0,0.4)";
        ctx.shadowBlur = 8;
        ctx.drawImage(wordmarkImg, 40, CANVAS_H - h - 40, w, h);
        ctx.restore();
    } else {
        ctx.textAlign = "left";
        ctx.fillStyle = "#FFF";
        ctx.font = `900 44px ${FONT_FOR_STYLE.BOLD}`;
        ctx.fillText("OfferNest", 40, CANVAS_H - 50);
    }
    // Shop logo bottom-right — always a 128px round crop
    const size = 128;
    const x = CANVAS_W - size - 40;
    const y = CANVAS_H - size - 40;
    ctx.save();
    ctx.beginPath();
    ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
    ctx.fillStyle = "#FFF";
    ctx.shadowColor = "rgba(0,0,0,0.3)";
    ctx.shadowBlur = 8;
    ctx.fill();
    ctx.restore();
    ctx.save();
    ctx.beginPath();
    ctx.arc(x + size / 2, y + size / 2, size / 2 - 6, 0, Math.PI * 2);
    ctx.clip();
    if (shopLogoImg) {
        ctx.drawImage(shopLogoImg, x + 6, y + 6, size - 12, size - 12);
    } else {
        ctx.fillStyle = "#FF7A1A";
        ctx.fillRect(x, y, size, size);
        ctx.textAlign = "center";
        ctx.fillStyle = "#FFF";
        ctx.font = `900 62px ${FONT_FOR_STYLE.BOLD}`;
        ctx.fillText(((shopName || "S").charAt(0) || "S").toUpperCase(), x + size / 2, y + size / 2 + 22);
    }
    ctx.restore();
};
